import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, MapPin, Check, ArrowRight, Star, Loader2, CheckCircle, Clock, CalendarCheck, Sparkles, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import FacebookFeed from '../components/FacebookFeed';
import BookingCalendar from '../components/BookingCalendar';
import CollectionAddress from '../components/CollectionAddress';
import { locations } from '../data/locations';
import { guides } from '../data/guides';
import { publicAPI } from '../services/api';
import useAPI from '../hooks/useAPI';
import SEO, { generateBusinessSchema } from '../components/SEO';

const Home = () => {
  const { data: motorhomes, loading: motorhomesLoading } = useAPI(() => publicAPI.getMotorhomes(), []);
  const { data: reviews, loading: reviewsLoading } = useAPI(() => publicAPI.getReviews(), []);
  const { data: googleData } = useAPI(() => publicAPI.getGoogleReviews(), []);
  
  const allMotorhomes = motorhomes || [];
  const comingSoon = allMotorhomes.filter((m) => m.availableFrom);
  const minPrice = allMotorhomes.length ? Math.min(...allMotorhomes.map((m) => m.pricePerNight)) : 120;
  const liveGoogle = googleData?.configured && googleData.reviews?.length > 0;
  const testimonials = (liveGoogle ? googleData.reviews : reviews)?.slice(0, 4) || [];
  const googleRating = googleData?.rating ? googleData.rating.toFixed(1) : '5.0';
  const googleCount = googleData?.userRatingCount || '25+';
  const googleMapsUri = googleData?.googleMapsUri || 'https://www.google.com/maps/search/?api=1&query=Millie%27s+Motorhome+Hire+Long+Melford';

  return (
    <div className="min-h-screen">
      <SEO 
        title="Motorhome Hire Suffolk & Essex | Luxury 4-6 Berth from £120/night"
        rawTitle
        description="Family-run motorhome hire in Suffolk & Essex. Luxury Roller Team 4, 5 & 6 berth motorhomes from £120/night - insurance, unlimited mileage & free parking included, pets welcome. Check live availability & book online."
        url="/"
        structuredData={[generateBusinessSchema(), { '@context': 'https://schema.org', '@type': 'WebSite', name: "Millie's Motorhome Hire", url: 'https://milliesmotorhomehire.co.uk' }]}
      />
      {/* Hero Section with Live Booking Calendar */}
      <section className="relative overflow-hidden bg-slate-900" id="book-online" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(2, 6, 23, 0.72), rgba(2, 6, 23, 0.72)), url('https://customer-assets.emergentagent.com/job_better-website-5/artifacts/0lmf72kn_Screenshot%202026-01-10%20185510.png')`,
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 lg:py-16 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          {/* Left: headline + 3 simple steps */}
          <div className="text-white lg:pt-6">
            <Badge className="mb-4 bg-orange-500 text-white hover:bg-orange-600 text-sm px-3 py-1">
              Live prices &amp; availability - book online in minutes
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
              Motorhome Hire in Suffolk &amp; Essex
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8">
              Millie's Motorhome Hire - family-run, luxury 4, 5 &amp; 6 berth Roller Team motorhomes. Unlimited mileage, insurance included, pets welcome.
            </p>

            <div className="space-y-3 mb-8" data-testid="hero-booking-steps">
              {[
                { n: 1, title: 'Pick a motorhome', text: 'Use the dropdown on the calendar' },
                { n: 2, title: 'Choose your dates', text: 'Green = available. Prices show instantly' },
                { n: 3, title: 'Click "Book Now"', text: 'Secure online checkout - done!' },
              ].map((s) => (
                <div key={s.n} className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15">
                  <div className="bg-orange-500 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold flex-shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <p className="font-semibold">{s.title}</p>
                    <p className="text-sm text-gray-300">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Link to="/motorhomes" data-testid="hero-view-fleet-btn">
                  View Our Fleet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <a href="https://wa.me/447527006966" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
                  <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white hover:text-slate-900">
                <a href="mailto:milliesmotorhomehire@gmail.com" data-testid="hero-email-btn">
                  Email Us
                </a>
              </Button>
            </div>
          </div>

          {/* Right: live booking calendar */}
          <Card className="shadow-2xl border-0 overflow-hidden scroll-mt-28" data-testid="hero-booking-card">
            <div className="bg-green-600 text-white px-6 py-4 flex items-center gap-3">
              <CalendarCheck className="h-7 w-7 flex-shrink-0" />
              <div>
                <h2 className="text-lg md:text-xl font-bold leading-tight">Check Prices, Availability &amp; Book Online</h2>
                <p className="text-sm text-green-100">Live calendar - select your motorhome, pick your dates, book instantly</p>
              </div>
            </div>
            <CardContent className="p-4 md:p-6 bg-white">
              <BookingCalendar categoryId="49050" months={2} defaultUnitId="50253" />
              <p className="text-xs text-slate-500 text-center mt-4">
                Collection &amp; drop-off at <a href="https://www.google.com/maps/search/?api=1&query=Clay+Hill+Farm+Clay+Hill+Lane+Wattisham+Suffolk+IP7+7JS" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600" data-testid="hero-address-link">Clay Hill Farm, Wattisham, Suffolk IP7 7JS</a>. Prefer to talk? Call <a href="tel:07527006966" className="font-semibold text-blue-600">07527 006966</a> or WhatsApp us.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Key Facts Strip */}
      <section className="bg-white border-b border-slate-200" data-testid="key-facts-strip">
        <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { icon: Star, label: `From £${minPrice} / night`, sub: 'All-inclusive pricing' },
            { icon: Clock, label: 'Collect 3pm · Return 11am', sub: 'Wattisham, Suffolk IP7 7JS' },
            { icon: CheckCircle, label: 'Insurance included', sub: 'Ages 25-75 (others by referral)' },
            { icon: MapPin, label: 'Unlimited mileage', sub: 'UK & Europe' },
            { icon: Check, label: 'Pets welcome', sub: 'From £20 per hire' },
            { icon: Car, label: 'Free car parking', sub: 'Leave your car with us' },
          ].map((f) => (
            <div key={f.label} className="flex items-start gap-3">
              <div className="bg-green-100 text-green-700 rounded-lg p-2 flex-shrink-0"><f.icon className="h-5 w-5" /></div>
              <div>
                <p className="font-semibold text-slate-900 text-sm leading-tight">{f.label}</p>
                <p className="text-xs text-slate-500">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Motorhomes */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Fleet</h2>
            <p className="text-xl text-slate-600">Choose from our complete selection of premium motorhomes</p>
          </div>

          {motorhomesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-slate-600">Loading fleet...</span>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {allMotorhomes.map((motorhome) => (
                <Card key={motorhome.id || motorhome.slug} className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={motorhome.images[0] + '?w=600'}
                      alt={motorhome.name}
                      className="w-full h-full object-cover transition-transform hover:scale-110 duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-full text-sm font-semibold">
                      {motorhome.berth} Berth
                    </div>
                    <div className="absolute top-4 right-4">
                      {motorhome.availableFrom ? (
                        <Badge className="bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1 animate-pulse">
                          <Calendar className="h-3 w-3" />
                          Book Now for {motorhome.availableFrom}!
                        </Badge>
                      ) : motorhome.available !== false ? (
                        <Badge className="bg-green-500 text-white hover:bg-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Available
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Check Dates
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-2">{motorhome.name}</h3>
                    <p className="text-slate-600 text-sm mb-4 capitalize">{motorhome.type}</p>
                    <p className="text-slate-700 mb-4 line-clamp-2">{motorhome.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-sm text-slate-600">From</span>
                        <span className="text-2xl font-bold text-blue-600 ml-2">£{motorhome.pricePerNight}</span>
                      </div>
                      <span className="text-slate-600 text-sm">per night</span>
                    </div>
                    <Button asChild className="w-full bg-green-600 hover:bg-green-700 font-bold">
                      <Link to={`/motorhomes/${motorhome.slug}?book=1`} data-testid={`fleet-check-availability-${motorhome.slug}`}>
                        <CalendarCheck className="mr-2 h-4 w-4" />
                        Check Prices & Availability
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full mt-2">
                      <Link to={`/motorhomes/${motorhome.slug}`} data-testid={`fleet-view-details-${motorhome.slug}`}>View Details</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/motorhomes">
                View Full Fleet Details
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* New for 2027 Spotlight */}
      {comingSoon.length > 0 && (
        <section className="py-16 bg-slate-900 text-white relative overflow-hidden" data-testid="new-for-2027-section">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-purple-600/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <Badge className="mb-3 bg-orange-500 text-white hover:bg-orange-600 text-sm px-3 py-1">
                  <Sparkles className="h-4 w-4 mr-1" /> NEW FOR 2027
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold">Brand New Zefiros Arriving April 2027</h2>
                <p className="text-slate-300 text-lg mt-2">Be the first to hire them - pre-book your dates today</p>
              </div>
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600 font-bold">
                <Link to="/#book-online" data-testid="new-2027-prebook-btn">
                  <CalendarCheck className="mr-2 h-5 w-5" />
                  Pre-Book Now
                </Link>
              </Button>
            </div>

            <div className={`grid md:grid-cols-2 gap-8 ${comingSoon.length >= 3 ? 'lg:grid-cols-3' : ''}`}>
              {comingSoon.map((m) => (
                <div key={m.id} className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-colors" data-testid={`new-2027-card-${m.slug}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img src={m.images[0]} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="bg-white text-slate-900 px-3 py-1 rounded-full text-sm font-semibold">{m.berth} Berth</span>
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold capitalize">{m.type}</span>
                    </div>
                    <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> From {m.availableFrom}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-2xl font-bold">{m.name}</h3>
                      <div className="text-right flex-shrink-0">
                        <span className="text-sm text-slate-300">From </span>
                        <span className="text-2xl font-bold text-orange-400">£{m.pricePerNight}</span>
                        <span className="text-sm text-slate-300">/night</span>
                      </div>
                    </div>
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-200 mb-5">
                      {m.features.slice(0, 4).map((f) => (
                        <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-green-400 flex-shrink-0" />{f}</li>
                      ))}
                    </ul>
                    <div className="flex gap-3">
                      <Button asChild className="flex-1 bg-green-600 hover:bg-green-700 font-bold">
                        <Link to={`/motorhomes/${m.slug}?book=1`} data-testid={`new-2027-book-${m.slug}`}>
                          <CalendarCheck className="mr-2 h-4 w-4" /> Pre-Book Dates
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white hover:text-slate-900">
                        <Link to={`/motorhomes/${m.slug}`} data-testid={`new-2027-details-${m.slug}`}>Details</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Hiring with Millie's is as easy as 1...2...3
            </h2>
            <p className="text-xl text-slate-600">
              Simple, transparent, and hassle-free motorhome hire
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-blue-50 rounded-2xl p-8 h-full transition-all hover:shadow-lg hover:-translate-y-1">
                <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                  1
                </div>
                <div className="flex justify-center mb-6">
                  <Search className="h-16 w-16 text-blue-600" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-center">Choose Your Motorhome</h3>
                <p className="text-slate-600 text-center">
                  Browse our selection of 4 and 6 berth motorhomes. All can be driven on a standard UK driving licence!
                </p>
                <div className="mt-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/2c453zzo_Screenshot%202026-01-11%20144116.png" 
                    alt="Motorhome selection"
                    className="rounded-lg w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-green-50 rounded-2xl p-8 h-full transition-all hover:shadow-lg hover:-translate-y-1">
                <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                  2
                </div>
                <div className="flex justify-center mb-6">
                  <Calendar className="h-16 w-16 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-center">Check Availability & Book</h3>
                <p className="text-slate-600 text-center">
                  Use the live calendar at the top of this page to check availability and pricing. Book online with our secure system!
                </p>
                <div className="mt-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/w5kobp2b_Screenshot%202026-01-11%20105212.png" 
                    alt="Interior booking"
                    className="rounded-lg w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-purple-50 rounded-2xl p-8 h-full transition-all hover:shadow-lg hover:-translate-y-1">
                <div className="bg-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                  3
                </div>
                <div className="flex justify-center mb-6">
                  <MapPin className="h-16 w-16 text-purple-600" />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-center">Enjoy Your Adventure</h3>
                <p className="text-slate-600 text-center">
                  Enjoy a stress-free holiday with unlimited mileage and full support from our family team!
                </p>
                <div className="mt-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/f9v2mgn3_Screenshot%202026-01-11%20104704.png" 
                    alt="Family camping"
                    className="rounded-lg w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What's Included With Every Hire</h2>
            <p className="text-xl text-slate-600">Everything you need for the perfect motorhome adventure - included in the price</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Kitchen Equipment */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Fully Equipped Kitchen</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Cookware & utensils</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Crockery & cutlery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Pots, pans & glassware</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Kettle</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Bedding & Towels - NOT INCLUDED */}
            <Card className="hover:shadow-lg transition-all border-2 border-amber-300 bg-amber-50">
              <CardContent className="p-6">
                <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center text-amber-800">Bedding & Towels - Optional Extra</h3>
                <p className="text-xs text-amber-700 text-center mb-3 font-medium">NOT included in hire price - available to add</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold flex-shrink-0">+</span>
                    <span>Bedding set: £30 per set (quilt, pillow, towel)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold flex-shrink-0">+</span>
                    <span>Or bring your own bedding & towels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Can be added at checkout</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Cleaning & Essentials */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Cleaning & Essentials</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Toilet chemicals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Cleaning supplies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Fresh water tank filled</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Levelling ramps</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Gas & Fuel */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Power & Fuel</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Full tank of diesel & AdBlue</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="font-semibold text-amber-700">Must be returned full</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>1x 6kg gas bottle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Mains leads & electric hook-up</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Cleaning & Chemicals */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-teal-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Cleaning & Waste</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Toilet chemicals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Cleaning products</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Waste bags</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Toilet rolls</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Water System */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Water System</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Fresh water filled</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Water container (Aqua Roll)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Water refill hose & attachments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Electric pump for refilling tank</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Entertainment */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-indigo-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Entertainment</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Avtex Smart TV</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>One or two TVs (depending on motorhome)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Included within price</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Premium viewing experience</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Insurance & Support */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Insurance & Travel</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Comprehensive insurance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>24/7 breakdown assistance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>European travel (extra charges)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Can be added at checkout</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Bike Rack */}
            <Card className="hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">Bike Rack (Optional)</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Holds up to 4 bikes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Secure rear mounting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Optional extra</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Can be added at extras section</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Free Car Parking */}
            <Card className="hover:shadow-lg transition-all border-2 border-green-300 bg-green-50">
              <CardContent className="p-6">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2 text-center text-green-800">Free Car Parking</h3>
                <p className="text-xs text-green-700 text-center mb-3 font-medium">Leave your car safely while you're away!</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Free parking on our premises</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Safe and secure location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Available throughout your hire</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>No additional cost</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Pet-Friendly Banner */}
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 mb-8 border-2 border-amber-200">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 rounded-full p-3">
                  <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">Pets Welcome!</h3>
                  <p className="text-slate-600">Well-behaved furry friends are part of the family</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-200">
                  <p className="text-sm text-slate-600 mb-1">1 Pet</p>
                  <p className="text-2xl font-bold text-amber-600">£20</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-200">
                  <p className="text-sm text-slate-600 mb-1">2 Pets</p>
                  <p className="text-2xl font-bold text-amber-600">£40</p>
                </div>
              </div>
              <p className="text-sm text-slate-500">Select pets via <strong>extras screen</strong> during booking</p>
            </div>
          </Card>

          {/* Collection & Drop-off Times Banner */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-8 border-2 border-green-200">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Clock className="h-6 w-6 text-green-600" />
              <h3 className="text-2xl font-bold text-slate-900">Standard Collection & Drop-off Times</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-4">
              <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-green-200">
                <p className="text-sm text-slate-600 mb-1">FREE Collection Time</p>
                <p className="text-4xl font-bold text-green-600">3:00 PM</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-200">
                <p className="text-sm text-slate-600 mb-1">FREE Drop-off Time</p>
                <p className="text-4xl font-bold text-amber-600">11:00 AM</p>
              </div>
            </div>
            <p className="text-center text-slate-600 text-sm mb-5">
              Times can be extended at an additional cost via the <strong>extras screen</strong> during booking
            </p>
            <div className="max-w-2xl mx-auto"><CollectionAddress /></div>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Plus Exclusive Benefits!</h3>
            <div className="grid md:grid-cols-3 gap-4 text-slate-700 max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span>Unlimited Mileage</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span>No Hidden Costs</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span>Secure Vehicle Parking</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span>Full Handover Demo</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span>C&CC Campsite Discounts</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span>Fire & Safety Equipment</span>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* How Insurance Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How Insurance Works</h2>
            <p className="text-xl text-slate-600">Insurance is supplied by us and included within the hire price</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Driver Requirements */}
            <Card className="border-blue-200">
              <CardContent className="p-8">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Driver Requirements</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">Age 25 - 75</p>
                      <p className="text-sm text-slate-600">All drivers must be within this age range</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">Licence held for 2+ years</p>
                      <p className="text-sm text-slate-600">Must have held manual or automatic licence for longer than 2 years</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">Clean licence record</p>
                      <p className="text-sm text-slate-600">Not had licence suspended in the last three years</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">No more than two three penalty convictions</p>
                      <p className="text-sm text-slate-600">Maximum penalty points on licence</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">Two proofs of address</p>
                      <p className="text-sm text-slate-600">Required for each driver on the hire</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Insurance Details */}
            <Card className="border-green-200">
              <CardContent className="p-8">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Insurance & Deposit</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">Insurance included</p>
                      <p className="text-sm text-slate-600">Comprehensive insurance supplied by us and included within the hire price</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">£1,000 damage deposit</p>
                      <p className="text-sm text-slate-600">Any damages deducted from deposit - returned within 7 days</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">Documents requested 7 days prior</p>
                      <p className="text-sm text-slate-600">All required documents will be requested 7 days before your hire start date</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center">
              <p className="text-slate-700 mb-2">
                <strong>Don't meet these requirements?</strong>
              </p>
              <p className="text-slate-600">
                We can refer your case to our insurers outside of these requirements. Please contact us to discuss your situation.
              </p>
              <Button asChild className="mt-4">
                <a href="mailto:milliesmotorhomehire@gmail.com">Get in Touch</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* On & Off Grid Capabilities */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">On & Off Grid Capabilities</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Our motorhomes are equipped to provide full comfort whether you're hooked up to mains or wild camping off-grid
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* On Grid */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all">
              <CardContent className="p-8">
                <div className="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-center">On Grid / Mains Hook-Up</h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>All appliances run from mains electricity</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Continuous power supply</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Leisure battery charging automatically</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Unlimited power for extended stays</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Ideal for campsites with electric hook-up</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Off Grid */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all">
              <CardContent className="p-8">
                <div className="bg-green-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-center">Off Grid / Wild Camping</h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>All appliances can run on gas (heating, cooking, hot water)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Large capacity leisure battery for lights and electronics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Avtex TVs work from leisure battery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Water pump operates on battery power</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Perfect for remote locations and wild camping</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="bg-white/5 backdrop-blur-sm border-white/20 max-w-4xl mx-auto">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Complete Flexibility</h3>
                <p className="text-blue-100 text-lg leading-relaxed">
                  Our motorhomes give you the freedom to choose your adventure. Stay at campsites with full facilities or explore remote locations off the beaten track. With dual power options (gas and electric) and high-capacity leisure batteries, you'll have everything you need wherever you go.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Millie's Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Millie's?</h2>
            <p className="text-xl text-slate-600">We make motorhome hire easy and enjoyable</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Unlimited Mileage', description: 'Explore as much as you want with no mileage restrictions' },
              { title: 'Family-Run Business', description: 'Personal service and genuine care for your experience' },
              { title: 'No Hidden Costs', description: 'Transparent pricing with everything included' },
              { title: '24/7 Support', description: 'Round-the-clock assistance throughout your hire' },
              { title: 'Fully Equipped', description: 'Everything you need for your adventure included' },
              { title: 'European Travel', description: 'Take your motorhome across to mainland Europe' },
              { title: 'Club Recommended', description: 'Official Camping & Caravanning Club approved supplier with site discounts' },
              { title: 'Special Discounts', description: '10% off for emergency services & armed forces' }
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas & Guides */}
      <section className="py-16 bg-white border-t" data-testid="areas-guides-section">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12">
          <div>
            <Badge className="mb-3 bg-blue-100 text-blue-800 hover:bg-blue-100"><MapPin className="h-3.5 w-3.5 mr-1" /> Suffolk &amp; Essex</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Motorhome hire across Suffolk &amp; Essex</h2>
            <p className="text-slate-600 mb-6">We're based at Wattisham in the heart of Suffolk - 25 minutes from Ipswich and Bury St Edmunds, 35 from Colchester and an hour from Chelmsford. Leave your car with us for free and start your holiday the moment you collect.</p>
            <div className="flex flex-wrap gap-2">
              {locations.map((l) => (
                <Link key={l.slug} to={`/motorhome-hire/${l.slug}`} className="text-sm bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-700 px-4 py-2 rounded-full font-medium transition-colors" data-testid={`area-link-${l.slug}`}>
                  Motorhome hire {l.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <Badge className="mb-3 bg-orange-100 text-orange-800 hover:bg-orange-100">Free guides</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Trip ideas, campsites &amp; first-timer tips</h2>
            <ul className="divide-y">
              {guides.slice(0, 4).map((g) => (
                <li key={g.slug} className="py-3">
                  <Link to={`/guides/${g.slug}`} data-testid={`guide-link-${g.slug}`} className="group flex items-start justify-between gap-4">
                    <span><span className="font-semibold text-slate-900 group-hover:text-blue-700">{g.title}</span><span className="block text-sm text-slate-500">{g.excerpt}</span></span>
                    <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-blue-700 flex-shrink-0 mt-1" />
                  </Link>
                </li>
              ))}
            </ul>
            <Button asChild variant="outline" className="mt-4"><Link to="/guides">All guides <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-yellow-500 text-slate-900 hover:bg-yellow-600" data-testid="google-rating-badge">
              ⭐ {googleRating} STAR RATING ON GOOGLE{liveGoogle ? ` · ${googleCount} REVIEWS` : ''}
            </Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-slate-600">{liveGoogle ? 'Latest reviews, live from Google' : 'Real experiences from real adventurers'}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="testimonials-grid">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 text-sm italic line-clamp-6">"{testimonial.text}"</p>
                  <div className="border-t pt-4 flex items-center gap-3">
                    {testimonial.photo && <img src={testimonial.photo} alt="" className="h-9 w-9 rounded-full" referrerPolicy="no-referrer" />}
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {testimonial.authorUri ? <a href={testimonial.authorUri} target="_blank" rel="noopener noreferrer" className="hover:underline">{testimonial.name}</a> : testimonial.name}
                      </p>
                      {testimonial.location && <p className="text-slate-600 text-xs">{testimonial.location}</p>}
                      <p className="text-slate-500 text-xs mt-1">
                        {testimonial.date}
                        {testimonial.googleMapsUri && <> · <a href={testimonial.googleMapsUri} target="_blank" rel="noopener noreferrer" className="underline">View on Google</a></>}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/reviews">
                Read All {googleCount} Google Reviews
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800">
              <a href={googleMapsUri} target="_blank" rel="noopener noreferrer" data-testid="google-maps-reviews-link">See us on Google Maps</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Camping & Caravanning Club Section */}
      <section className="py-16 bg-gradient-to-r from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge className="mb-4 bg-red-600 text-white hover:bg-red-700">
                OFFICIAL RECOMMENDED HIRER
              </Badge>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Camping & Caravanning Club Recommended
              </h2>
              <p className="text-xl text-slate-700 mb-6">
                We are proud to be an approved and recommended motorhome hire supplier by The Camping and Caravanning Club - The Friendly Club.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Exclusive Discounts</p>
                    <p className="text-slate-600">Hiring from us gives you access to discounted rates at participating club campsites throughout the UK</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Trusted Quality</p>
                    <p className="text-slate-600">As a recommended supplier, we meet the club's high standards for vehicle quality and customer service</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Nationwide Network</p>
                    <p className="text-slate-600">Access to hundreds of certificated locations and club sites across the UK</p>
                  </div>
                </div>
              </div>
              <Button asChild size="lg" className="bg-red-600 hover:bg-red-700">
                <a href="https://www.campingandcaravanningclub.co.uk/" target="_blank" rel="noopener noreferrer">
                  Learn More About The Club
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <div className="flex justify-center">
              <Card className="p-8 bg-white shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/sseihcg6_Screenshot%202026-01-11%20103414.png"
                  alt="The Camping and Caravanning Club - The Friendly Club"
                  className="w-full max-w-md"
                />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Facebook Feed Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Latest News & Updates</h2>
            <p className="text-xl text-slate-600">Follow our adventures and see what's happening at Millie's</p>
          </div>
          <div className="flex justify-center">
            <FacebookFeed />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Adventure?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Book your motorhome today and experience the freedom of the open road
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600 font-bold">
              <Link to="/#book-online" data-testid="cta-book-online-btn">
                <CalendarCheck className="mr-2 h-5 w-5" />
                Check Prices & Book Online
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/motorhomes">Browse Motorhomes</Link>
            </Button>
            <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
              <a href="https://wa.me/447527006966" target="_blank" rel="noopener noreferrer">WhatsApp Us</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              <a href="mailto:milliesmotorhomehire@gmail.com">Contact Us</a>
            </Button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
